import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    Loader2,
    Lock,
    Check,
    Upload,
    Trash2,
    Plus,
    Flag,
    Lightbulb,
    ClipboardCheck,
    FileText,
    ArrowRight,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";

const FINDING_COLUMNS = [
    { key: "strength", label: "Strengths", color: "#2E5D3A" },
    { key: "gap", label: "Gaps", color: "#B8641F" },
    { key: "systems_misalignment", label: "Systems Misalignment", color: "#5B2B7B" },
    { key: "continuous_improvement", label: "Continuous Improvement", color: "#0F7B8A" },
];

const PRIORITY_META = {
    high: { label: "High", className: "bg-red-50 text-red-700 border-red-200" },
    medium: { label: "Med", className: "bg-amber-50 text-amber-800 border-amber-200" },
    low: { label: "Low", className: "bg-slate-50 text-slate-700 border-slate-200" },
};

const EVIDENCE_TYPES = [
    ["strategic_document", "Strategic document"],
    ["leadership_artifact", "Leadership artifact"],
    ["interview_data", "Interview data"],
    ["observation_data", "Observation data"],
    ["data_report", "Data report"],
    ["communication_artifact", "Communication artifact"],
    ["budget_document", "Budget document"],
    ["curriculum_document", "Curriculum document"],
    ["assessment_artifact", "Assessment artifact"],
    ["student_work_sample", "Student work sample"],
    ["plc_documentation", "PLC documentation"],
    ["other", "Other"],
];

function useDebounced(fn, delayMs) {
    const timer = useRef();
    const fnRef = useRef(fn);
    fnRef.current = fn;
    return useCallback(
        (...args) => {
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => fnRef.current(...args), delayMs);
        },
        [delayMs],
    );
}

export default function DomainAssessmentPage() {
    const { cycleId, domainKey } = useParams();
    const navigate = useNavigate();
    const [daId, setDaId] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);

    const domain = data?.domain;
    const writable = !!data?.writable;
    const locked = data?.cycle?.status === "locked";

    const load = useCallback(async () => {
        try {
            setLoading(true);
            // Resolve DA id from cycle
            const cycleResp = await api.get(`/cycles/${cycleId}`);
            const match = cycleResp.data.domain_assessments.find(
                (x) => x.domain?.key === domainKey,
            );
            if (!match) {
                setError("Domain not found in this cycle");
                return;
            }
            setDaId(match.id);
            const { data: full } = await api.get(`/domain-assessments/${match.id}`);
            setData(full);
            setError(null);
        } catch (e) {
            setError(extractError(e, "Could not load domain assessment"));
        } finally {
            setLoading(false);
        }
    }, [cycleId, domainKey]);

    useEffect(() => {
        load();
    }, [load]);

    // ---- Rating update (debounced) ----
    const [pending, setPending] = useState({}); // {lf_id: {rating, notes}}
    const debouncedSave = useDebounced(async (lfId, next) => {
        if (!daId) return;
        setSaving(true);
        try {
            await api.put(`/domain-assessments/${daId}/look-fors/${lfId}/rating`, next);
            setLastSavedAt(new Date());
        } catch (e) {
            toast.error(extractError(e, "Could not save rating"));
        } finally {
            setSaving(false);
        }
    }, 800);

    const currentValue = (lf) => {
        const p = pending[lf.id];
        if (p) return p;
        return { rating: lf.rating, notes: lf.notes || "" };
    };

    const updateLookFor = (lf, patch) => {
        const cur = currentValue(lf);
        const next = { ...cur, ...patch };
        setPending((prev) => ({ ...prev, [lf.id]: next }));
        // Optimistic UI on scores: mutate data in place
        setData((prev) => {
            if (!prev) return prev;
            const clone = { ...prev, indicators: prev.indicators.map((ind) => ({
                ...ind,
                look_fors: ind.look_fors.map((row) =>
                    row.id === lf.id ? { ...row, ...next } : row,
                ),
            })) };
            // Recompute indicator + domain scores
            const indScores = clone.indicators.map((ind) => {
                const vals = ind.look_fors.map((r) => r.rating).filter((v) => v != null);
                ind.indicator_score = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
                return ind.indicator_score;
            });
            const nonNull = indScores.filter((v) => v != null);
            clone.computed_domain_score = nonNull.length ? nonNull.reduce((a, b) => a + b, 0) / nonNull.length : null;
            clone.rated_count = clone.indicators.reduce((n, ind) => n + ind.look_fors.filter((r) => r.rating != null).length, 0);
            return clone;
        });
        debouncedSave(lf.id, next);
    };

    if (loading) {
        return (
            <div className="text-[#0B2545] flex items-center gap-3">
                <Loader2 className="animate-spin" size={18} /> Loading domain…
            </div>
        );
    }
    if (error || !data) {
        return <div className="text-red-700">{error}</div>;
    }

    return (
        <div className="space-y-6" data-testid="domain-assessment-page">
            {/* Header */}
            <div>
                <Link to={`/app/cycles/${cycleId}`} className="text-xs text-[#1E56A0] hover:underline">
                    ← Back to cycle
                </Link>
                <div
                    className="mt-3 rounded-2xl overflow-hidden shadow-lg shadow-[#0B2545]/5"
                    data-testid="domain-header"
                >
                    <div
                        className="px-6 py-5 text-white"
                        style={{ backgroundColor: domain.color_hex }}
                    >
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold opacity-90">
                            Domain {domain.position}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-1">
                            <h1
                                className="font-serif text-3xl sm:text-4xl font-semibold leading-tight"
                                data-testid="domain-name"
                            >
                                {domain.name}
                            </h1>
                            {locked && (
                                <Badge className="bg-white/20 border-white/30 text-white border">
                                    <Lock size={12} className="mr-1" /> Locked
                                </Badge>
                            )}
                        </div>
                        <p className="mt-3 text-sm text-white/85 max-w-3xl leading-relaxed">
                            {domain.definition}
                        </p>
                    </div>
                    <div className="bg-white px-6 py-3 border-t border-[#D6E6F2] flex flex-wrap items-center gap-6 justify-between">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.25em] text-[#1E56A0] font-bold">
                                Current domain score
                            </div>
                            <div
                                className="font-serif text-2xl font-semibold text-[#0B2545]"
                                data-testid="domain-score"
                            >
                                {data.computed_domain_score != null
                                    ? `${data.computed_domain_score.toFixed(2)} / 4.0`
                                    : "—"}
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.25em] text-[#1E56A0] font-bold">
                                Look-fors rated
                            </div>
                            <div className="font-serif text-2xl font-semibold text-[#0B2545]">
                                {data.rated_count} / {data.total_look_fors}
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.25em] text-[#1E56A0] font-bold">
                                Status
                            </div>
                            <div className="font-serif text-2xl font-semibold text-[#0B2545] capitalize">
                                {data.status.replace(/_/g, " ")}
                            </div>
                        </div>
                        <div className="text-xs text-[#0E1B2C]/60">
                            {saving ? (
                                <span className="inline-flex items-center gap-1 text-amber-700">
                                    <Loader2 size={12} className="animate-spin" /> Saving…
                                </span>
                            ) : lastSavedAt ? (
                                <span className="inline-flex items-center gap-1 text-emerald-700">
                                    <Check size={12} /> Saved
                                </span>
                            ) : (
                                <span>Auto-saves as you type</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {!writable && !locked && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 text-sm flex items-center gap-2">
                    <AlertCircle size={14} /> You have read-only access to this domain assessment.
                </div>
            )}

            <Tabs defaultValue={domain.key === "instructional" ? "teachers" : "scoring"} className="w-full">
                <TabsList className="flex flex-wrap gap-1 bg-[#F1F6FB] h-auto p-1" data-testid="da-tabs">
                    {domain.key === "instructional" ? (
                        <TabsTrigger value="teachers" data-testid="tab-teachers">Teachers</TabsTrigger>
                    ) : (
                        <TabsTrigger value="scoring" data-testid="tab-scoring">Scoring</TabsTrigger>
                    )}
                    <TabsTrigger value="evidence" data-testid="tab-evidence">Evidence</TabsTrigger>
                    <TabsTrigger value="findings" data-testid="tab-findings">Findings</TabsTrigger>
                    <TabsTrigger value="red-flags" data-testid="tab-red-flags">Red Flags</TabsTrigger>
                    <TabsTrigger value="recommendations" data-testid="tab-recommendations">Recommendations</TabsTrigger>
                    <TabsTrigger value="guidance" data-testid="tab-guidance">Rating Guidance</TabsTrigger>
                </TabsList>

                {domain.key === "instructional" ? (
                    <TabsContent value="teachers">
                        <div className="rounded-2xl bg-white border border-[#D6E6F2] p-6 text-center space-y-3" data-testid="instructional-teachers-cta">
                            <p className="text-[#0E1B2C]/80">
                                Instructional Core is scored per teacher. Open the teacher roster to score individuals; the domain overall rolls up automatically.
                            </p>
                            <Link
                                to={`/app/cycles/${data.cycle.id}/domains/instructional/teachers`}
                                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-semibold"
                                style={{ backgroundColor: domain.color_hex }}
                                data-testid="go-to-teachers-btn"
                            >
                                Open teacher roster
                            </Link>
                        </div>
                    </TabsContent>
                ) : (
                    <TabsContent value="scoring">
                        <ScoringTab
                            data={data}
                            writable={writable}
                            updateLookFor={updateLookFor}
                            onCompleteRequest={async () => {
                                try {
                                    await api.post(`/domain-assessments/${daId}/complete`);
                                    toast.success("Domain marked complete");
                                    load();
                                } catch (e) {
                                    toast.error(extractError(e, "Could not complete domain"));
                                }
                            }}
                        />
                    </TabsContent>
                )}

                <TabsContent value="evidence">
                    <EvidenceTab
                        daId={daId}
                        writable={writable}
                        color={domain.color_hex}
                        indicators={data.indicators}
                        evidence={data.evidence}
                        onChange={load}
                    />
                </TabsContent>

                <TabsContent value="findings">
                    <FindingsTab
                        daId={daId}
                        writable={writable}
                        findings={data.findings}
                        onChange={load}
                    />
                </TabsContent>

                <TabsContent value="red-flags">
                    <RedFlagsTab
                        daId={daId}
                        writable={writable}
                        templates={data.red_flag_templates}
                        selected={data.red_flags}
                        color={domain.color_hex}
                        onChange={load}
                    />
                </TabsContent>

                <TabsContent value="recommendations">
                    <RecommendationsTab
                        daId={daId}
                        writable={writable}
                        recommendations={data.recommendations}
                        onChange={load}
                    />
                </TabsContent>

                <TabsContent value="guidance">
                    <RatingGuidanceTab guidance={data.rating_guidance} color={domain.color_hex} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

/* ---------------- Scoring Tab ---------------- */
function ScoringTab({ data, writable, updateLookFor, onCompleteRequest }) {
    return (
        <div className="space-y-6" data-testid="scoring-tab">
            <div className="flex justify-end">
                <Button
                    onClick={onCompleteRequest}
                    disabled={
                        !writable ||
                        data.rated_count < data.total_look_fors ||
                        data.status === "complete"
                    }
                    className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white"
                    data-testid="complete-domain-btn"
                >
                    <ClipboardCheck size={14} />
                    {data.status === "complete" ? "Domain complete" : "Mark domain complete"}
                </Button>
            </div>
            {data.indicators.map((ind) => (
                <IndicatorSection
                    key={ind.id}
                    ind={ind}
                    writable={writable}
                    updateLookFor={updateLookFor}
                    color={data.domain.color_hex}
                />
            ))}
        </div>
    );
}

function IndicatorSection({ ind, writable, updateLookFor, color }) {
    return (
        <div className="rounded-2xl bg-white border border-[#D6E6F2] overflow-hidden" data-testid={`indicator-${ind.position}`}>
            <div className="px-5 py-4 border-b border-[#D6E6F2] flex flex-wrap items-center justify-between gap-3" style={{ background: `${color}0d` }}>
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold" style={{ color }}>
                        Indicator {ind.position}
                    </div>
                    <div className="font-serif text-lg text-[#0B2545] font-semibold leading-snug">
                        {ind.name}
                    </div>
                </div>
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-[#1E56A0] font-bold">
                        Indicator score
                    </div>
                    <div className="font-serif text-xl font-semibold text-[#0B2545]">
                        {ind.indicator_score != null ? ind.indicator_score.toFixed(2) : "—"}
                    </div>
                </div>
            </div>
            <div className="divide-y divide-[#D6E6F2]">
                {ind.look_fors.map((lf) => (
                    <LookForRow
                        key={lf.id}
                        lf={lf}
                        writable={writable}
                        onChange={(patch) => updateLookFor(lf, patch)}
                        color={color}
                    />
                ))}
            </div>
        </div>
    );
}

function LookForRow({ lf, writable, onChange, color }) {
    return (
        <div className="px-5 py-4 grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid={`lookfor-${lf.id}`}>
            <div className="lg:col-span-5">
                <div className="font-semibold text-[#0B2545]">{lf.position}. {lf.name}</div>
                <div className="mt-1 text-sm text-[#0E1B2C]/70 leading-relaxed">{lf.description}</div>
            </div>
            <div className="lg:col-span-3">
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                    Rating
                </div>
                <RadioGroup
                    className="flex items-center gap-3"
                    value={lf.rating != null ? String(lf.rating) : ""}
                    onValueChange={(v) => onChange({ rating: parseInt(v, 10) })}
                    disabled={!writable}
                >
                    {[1, 2, 3, 4].map((n) => (
                        <label
                            key={n}
                            htmlFor={`${lf.id}-r${n}`}
                            className={`flex items-center gap-1 cursor-pointer text-sm ${
                                !writable ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                            data-testid={`rating-${lf.id}-${n}`}
                        >
                            <RadioGroupItem
                                id={`${lf.id}-r${n}`}
                                value={String(n)}
                                disabled={!writable}
                                style={lf.rating === n ? { borderColor: color, color } : {}}
                            />
                            {n}
                        </label>
                    ))}
                    {writable && lf.rating != null && (
                        <button
                            type="button"
                            className="text-xs text-[#0E1B2C]/60 underline hover:text-[#0B2545]"
                            onClick={() => onChange({ rating: null })}
                            data-testid={`clear-rating-${lf.id}`}
                        >
                            clear
                        </button>
                    )}
                </RadioGroup>
            </div>
            <div className="lg:col-span-4">
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                    Notes
                </div>
                <Textarea
                    className="min-h-[64px] bg-[#F8FBFF] border-[#D6E6F2]"
                    placeholder="Optional evidence, examples, quotes…"
                    value={lf.notes || ""}
                    onChange={(e) => onChange({ notes: e.target.value })}
                    disabled={!writable}
                    data-testid={`notes-${lf.id}`}
                />
            </div>
        </div>
    );
}

/* ---------------- Evidence Tab ---------------- */
function EvidenceTab({ daId, writable, color, indicators, evidence, onChange }) {
    const [uploadOpen, setUploadOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [evidenceType, setEvidenceType] = useState("other");
    const [label, setLabel] = useState("");
    const [lookForId, setLookForId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [filterLf, setFilterLf] = useState("all");
    const filtered = filterLf === "all"
        ? evidence
        : evidence.filter((e) => (e.look_for_id || "__none__") === filterLf);

    const flatLookFors = indicators.flatMap((i) => i.look_fors.map((lf) => ({ ...lf, indPos: i.position })));

    const handleUpload = async () => {
        if (!file) {
            toast.error("Choose a file");
            return;
        }
        setSubmitting(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("evidence_type", evidenceType);
        fd.append("label", label);
        if (lookForId && lookForId !== "__none__") fd.append("look_for_id", lookForId);
        try {
            await api.post(`/domain-assessments/${daId}/evidence`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Evidence uploaded");
            setUploadOpen(false);
            setFile(null);
            setLabel("");
            setLookForId("");
            setEvidenceType("other");
            onChange();
        } catch (e) {
            toast.error(extractError(e, "Upload failed"));
        } finally {
            setSubmitting(false);
        }
    };

    const doDelete = async (id) => {
        if (!window.confirm("Delete this evidence file?")) return;
        try {
            await api.delete(`/evidence/${id}`);
            toast.success("Evidence deleted");
            onChange();
        } catch (e) {
            toast.error(extractError(e, "Delete failed"));
        }
    };

    return (
        <div className="space-y-4" data-testid="evidence-tab">
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="text-sm text-[#0E1B2C]/70">
                    Upload PDF, DOCX, PNG or JPG up to 10 MB. Files are stored securely.
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={filterLf} onValueChange={setFilterLf}>
                        <SelectTrigger className="h-9 w-56" data-testid="evidence-filter">
                            <SelectValue placeholder="Filter by look-for" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All evidence</SelectItem>
                            <SelectItem value="__none__">Domain-level (unlinked)</SelectItem>
                            {flatLookFors.map((lf) => (
                                <SelectItem key={lf.id} value={lf.id}>
                                    {lf.indPos}.{lf.position} {lf.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="rounded-full text-white h-9 px-4"
                                style={{ backgroundColor: color }}
                                disabled={!writable}
                                data-testid="evidence-upload-btn"
                            >
                                <Upload size={14} /> Upload evidence
                            </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="evidence-upload-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl text-[#0B2545]">
                                    Upload evidence
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label>File</Label>
                                    <Input
                                        type="file"
                                        accept=".pdf,.docx,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                        data-testid="evidence-file-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Label (optional)</Label>
                                    <Input
                                        value={label}
                                        onChange={(e) => setLabel(e.target.value)}
                                        placeholder="e.g. Strategic Plan 2025-2028"
                                        data-testid="evidence-label-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Evidence type</Label>
                                    <Select value={evidenceType} onValueChange={setEvidenceType}>
                                        <SelectTrigger data-testid="evidence-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {EVIDENCE_TYPES.map(([v, l]) => (
                                                <SelectItem key={v} value={v}>
                                                    {l}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Attach to look-for (optional)</Label>
                                    <Select value={lookForId} onValueChange={setLookForId}>
                                        <SelectTrigger data-testid="evidence-lookfor-select">
                                            <SelectValue placeholder="Domain-level (no specific look-for)" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">Domain-level (no specific look-for)</SelectItem>
                                            {flatLookFors.map((lf) => (
                                                <SelectItem key={lf.id} value={lf.id}>
                                                    {lf.indPos}.{lf.position} {lf.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    onClick={handleUpload}
                                    disabled={submitting || !file}
                                    className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white"
                                    data-testid="evidence-upload-submit"
                                >
                                    {submitting ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                                    Upload
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#8AC4FF] bg-white p-8 text-center">
                    <FileText size={24} className="mx-auto text-[#1E56A0]" />
                    <p className="mt-2 text-[#0B2545] font-semibold">No evidence uploaded yet.</p>
                </div>
            ) : (
                <ul className="space-y-3" data-testid="evidence-list">
                    {filtered.map((e) => (
                        <li
                            key={e.id}
                            className="rounded-lg bg-white border border-[#D6E6F2] px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between"
                            data-testid={`evidence-item-${e.id}`}
                        >
                            <div className="min-w-0">
                                <div className="font-semibold text-[#0B2545] truncate">{e.filename}</div>
                                <div className="mt-1 flex flex-wrap gap-2 items-center text-xs text-[#0E1B2C]/70">
                                    <Badge className="bg-slate-100 text-slate-700 border-slate-200 border" variant="outline">
                                        {e.evidence_type.replace(/_/g, " ")}
                                    </Badge>
                                    {e.label && <span>· {e.label}</span>}
                                    <span>· {(e.size_bytes / 1024).toFixed(1)} KB</span>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-full border-[#1E56A0] text-[#1E56A0] hover:bg-[#D6E6F2]"
                                    onClick={async () => {
                                        try {
                                            const resp = await api.get(`/evidence/${e.id}/download`, {
                                                responseType: "blob",
                                            });
                                            const url = URL.createObjectURL(resp.data);
                                            const a = document.createElement("a");
                                            a.href = url;
                                            a.download = e.filename;
                                            document.body.appendChild(a);
                                            a.click();
                                            a.remove();
                                            URL.revokeObjectURL(url);
                                        } catch (err) {
                                            toast.error(extractError(err, "Download failed"));
                                        }
                                    }}
                                    data-testid={`evidence-download-${e.id}`}
                                >
                                    Download
                                </Button>
                                {writable && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => doDelete(e.id)}
                                        className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
                                        data-testid={`evidence-delete-${e.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/* ---------------- Findings Tab ---------------- */
function FindingsTab({ daId, writable, findings, onChange }) {
    const [drafts, setDrafts] = useState({}); // {category: text}

    const addFinding = async (category) => {
        const text = (drafts[category] || "").trim();
        if (!text) return;
        try {
            await api.post(`/domain-assessments/${daId}/findings`, { category, text });
            setDrafts((d) => ({ ...d, [category]: "" }));
            onChange();
        } catch (e) {
            toast.error(extractError(e, "Could not add finding"));
        }
    };

    const removeFinding = async (id) => {
        try {
            await api.delete(`/findings/${id}`);
            onChange();
        } catch (e) {
            toast.error(extractError(e, "Delete failed"));
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="findings-tab">
            {FINDING_COLUMNS.map((col) => {
                const rows = findings.filter((f) => f.category === col.key);
                return (
                    <div
                        key={col.key}
                        className="rounded-2xl bg-white border border-[#D6E6F2] flex flex-col"
                        data-testid={`findings-column-${col.key}`}
                    >
                        <div className="px-4 py-3 border-b border-[#D6E6F2] flex items-center gap-2" style={{ color: col.color }}>
                            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: col.color }} />
                            <div className="font-serif font-semibold">{col.label}</div>
                            <div className="ml-auto text-xs text-[#0E1B2C]/60">{rows.length}</div>
                        </div>
                        <ul className="flex-1 p-3 space-y-2 min-h-[64px]">
                            {rows.map((r) => (
                                <li
                                    key={r.id}
                                    className="group rounded-lg bg-[#F8FBFF] border border-[#D6E6F2] p-3 text-sm relative"
                                    data-testid={`finding-${r.id}`}
                                >
                                    <div className="pr-6 whitespace-pre-wrap">{r.text}</div>
                                    {writable && (
                                        <button
                                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800"
                                            onClick={() => removeFinding(r.id)}
                                            data-testid={`finding-delete-${r.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                        {writable && (
                            <div className="p-3 border-t border-[#D6E6F2] space-y-2">
                                <Textarea
                                    placeholder={`Add a ${col.label.toLowerCase().replace(/s$/, "")}…`}
                                    className="min-h-[54px]"
                                    value={drafts[col.key] || ""}
                                    onChange={(e) => setDrafts((d) => ({ ...d, [col.key]: e.target.value }))}
                                    data-testid={`finding-input-${col.key}`}
                                />
                                <Button
                                    size="sm"
                                    onClick={() => addFinding(col.key)}
                                    disabled={!(drafts[col.key] || "").trim()}
                                    className="rounded-full w-full text-white"
                                    style={{ backgroundColor: col.color }}
                                    data-testid={`finding-add-${col.key}`}
                                >
                                    <Plus size={14} /> Add
                                </Button>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/* ---------------- Red Flags Tab ---------------- */
function RedFlagsTab({ daId, writable, templates, selected, color, onChange }) {
    const [customText, setCustomText] = useState("");
    const [customIndicates, setCustomIndicates] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const selectedByTemplate = new Map(selected.filter((s) => s.red_flag_template_id).map((s) => [s.red_flag_template_id, s]));

    const toggleTemplate = async (tpl) => {
        const existing = selectedByTemplate.get(tpl.id);
        if (existing) {
            await api.delete(`/red-flags/${existing.id}`);
        } else {
            await api.post(`/domain-assessments/${daId}/red-flags`, { red_flag_template_id: tpl.id });
        }
        onChange();
    };

    const addCustom = async () => {
        if (!customText.trim()) return;
        setSubmitting(true);
        try {
            await api.post(`/domain-assessments/${daId}/red-flags`, {
                text: customText.trim(),
                indicates: customIndicates.trim(),
            });
            setCustomText("");
            setCustomIndicates("");
            onChange();
        } catch (e) {
            toast.error(extractError(e, "Could not add red flag"));
        } finally {
            setSubmitting(false);
        }
    };

    const removeCustom = async (id) => {
        try {
            await api.delete(`/red-flags/${id}`);
            onChange();
        } catch (e) {
            toast.error(extractError(e, "Delete failed"));
        }
    };

    return (
        <div className="space-y-6" data-testid="red-flags-tab">
            <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5">
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                    Common red flags for this domain
                </div>
                <ul className="space-y-2" data-testid="red-flag-templates-list">
                    {templates.map((tpl) => {
                        const isSelected = selectedByTemplate.has(tpl.id);
                        return (
                            <li key={tpl.id} className="rounded-lg border border-[#D6E6F2] px-4 py-3 flex items-start gap-3 bg-[#F8FBFF]">
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleTemplate(tpl)}
                                    disabled={!writable}
                                    className="mt-1 h-4 w-4 rounded"
                                    style={{ accentColor: color }}
                                    data-testid={`red-flag-template-${tpl.id}`}
                                />
                                <div className="min-w-0 text-sm">
                                    <div className="font-semibold text-[#0B2545]">{tpl.text}</div>
                                    <div className="text-[#0E1B2C]/70">→ {tpl.indicates}</div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
            {writable && (
                <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="red-flag-custom-form">
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                        Add your own
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Observation</Label>
                            <Input
                                value={customText}
                                onChange={(e) => setCustomText(e.target.value)}
                                placeholder="Describe the pattern you see"
                                data-testid="red-flag-custom-text"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>What it indicates</Label>
                            <Input
                                value={customIndicates}
                                onChange={(e) => setCustomIndicates(e.target.value)}
                                placeholder="What this indicates"
                                data-testid="red-flag-custom-indicates"
                            />
                        </div>
                    </div>
                    <Button
                        onClick={addCustom}
                        disabled={submitting || !customText.trim()}
                        className="mt-3 rounded-full text-white"
                        style={{ backgroundColor: color }}
                        data-testid="red-flag-custom-add"
                    >
                        <Plus size={14} /> Add red flag
                    </Button>
                </div>
            )}
            {selected.filter((s) => !s.red_flag_template_id).length > 0 && (
                <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5">
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                        Custom red flags
                    </div>
                    <ul className="space-y-2">
                        {selected
                            .filter((s) => !s.red_flag_template_id)
                            .map((s) => (
                                <li key={s.id} className="rounded-lg border border-[#D6E6F2] px-4 py-3 flex items-start justify-between bg-[#F8FBFF]" data-testid={`red-flag-custom-${s.id}`}>
                                    <div className="text-sm">
                                        <div className="font-semibold text-[#0B2545]">{s.text}</div>
                                        {s.indicates && <div className="text-[#0E1B2C]/70">→ {s.indicates}</div>}
                                    </div>
                                    {writable && (
                                        <button
                                            className="text-red-600 hover:text-red-800"
                                            onClick={() => removeCustom(s.id)}
                                            data-testid={`red-flag-remove-${s.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </li>
                            ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/* ---------------- Recommendations Tab ---------------- */
function RecommendationsTab({ daId, writable, recommendations, onChange }) {
    const [text, setText] = useState("");
    const [priority, setPriority] = useState("medium");
    const [submitting, setSubmitting] = useState(false);

    const addRec = async () => {
        if (!text.trim()) return;
        setSubmitting(true);
        try {
            await api.post(`/domain-assessments/${daId}/recommendations`, { text: text.trim(), priority });
            setText("");
            setPriority("medium");
            onChange();
        } catch (e) {
            toast.error(extractError(e, "Could not add"));
        } finally {
            setSubmitting(false);
        }
    };

    const removeRec = async (id) => {
        try {
            await api.delete(`/recommendations/${id}`);
            onChange();
        } catch (e) {
            toast.error(extractError(e, "Delete failed"));
        }
    };

    const updateRec = async (id, patch) => {
        try {
            await api.put(`/recommendations/${id}`, patch);
            onChange();
        } catch (e) {
            toast.error(extractError(e, "Update failed"));
        }
    };

    return (
        <div className="space-y-4" data-testid="recommendations-tab">
            {writable && (
                <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="recommendation-form">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="md:col-span-3 space-y-2">
                            <Label>Recommendation</Label>
                            <Textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="What should the district do next?"
                                className="min-h-[64px]"
                                data-testid="recommendation-text"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Priority</Label>
                            <Select value={priority} onValueChange={setPriority}>
                                <SelectTrigger data-testid="recommendation-priority">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="low">Low</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button
                                onClick={addRec}
                                disabled={submitting || !text.trim()}
                                className="w-full rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white"
                                data-testid="recommendation-add"
                            >
                                <Plus size={14} /> Add
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {recommendations.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#8AC4FF] bg-white p-8 text-center">
                    <Lightbulb size={24} className="mx-auto text-[#1E56A0]" />
                    <p className="mt-2 text-[#0B2545] font-semibold">No recommendations yet.</p>
                </div>
            ) : (
                <ul className="space-y-2" data-testid="recommendations-list">
                    {recommendations.map((r) => {
                        const p = PRIORITY_META[r.priority] || PRIORITY_META.medium;
                        return (
                            <li
                                key={r.id}
                                className="rounded-lg bg-white border border-[#D6E6F2] px-4 py-3 flex items-start gap-3"
                                data-testid={`recommendation-${r.id}`}
                            >
                                <Badge className={p.className + " border shrink-0"} variant="outline">
                                    {p.label}
                                </Badge>
                                <div className="flex-1 text-sm text-[#0E1B2C]/85 whitespace-pre-wrap">{r.text}</div>
                                {writable && (
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Select
                                            value={r.priority}
                                            onValueChange={(v) => updateRec(r.id, { priority: v })}
                                        >
                                            <SelectTrigger className="h-7 w-24 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="high">High</SelectItem>
                                                <SelectItem value="medium">Medium</SelectItem>
                                                <SelectItem value="low">Low</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <button
                                            className="text-red-600 hover:text-red-800"
                                            onClick={() => removeRec(r.id)}
                                            data-testid={`recommendation-delete-${r.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

/* ---------------- Rating Guidance Tab ---------------- */
function RatingGuidanceTab({ guidance, color }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="guidance-tab">
            {guidance.map((g) => (
                <div key={g.rating} className="rounded-2xl bg-white border border-[#D6E6F2] overflow-hidden">
                    <div className="px-5 py-3 text-white flex items-center gap-3" style={{ backgroundColor: color }}>
                        <div className="h-8 w-8 rounded-full bg-white/20 border border-white/30 flex items-center justify-center font-serif font-bold">
                            {g.rating}
                        </div>
                        <div className="font-serif font-semibold text-lg">{g.label}</div>
                    </div>
                    <div className="p-5 text-sm text-[#0E1B2C]/85 leading-relaxed">{g.characteristics}</div>
                </div>
            ))}
        </div>
    );
}
